// src/pages/TrainingPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { v4 as uuidv4 } from 'uuid';
import TrainingCalendar from '../components/calendar/TrainingCalendar';
import TrainingList from '../components/training/TrainingList';

const TrainingPage = () => {
    const { user } = useAuth();
    const navigate = useNavigate();

    const [trainings, setTrainings] = useState([
        {
            id: 1,
            user_id: 1,
            type: 'Carrera Continua', // Asegurado que el tipo sea válido
            description: 'Carrera suave',
            date: new Date().toISOString(),
            duration: 60,
            distance: 10,
            avgPace: '5:00',
            avgHeartRate: 160,
            rpe: 6,
            notes: 'Buen entrenamiento'
        },
        {
            id: uuidv4(),
            user_id: 1,
            type: 'Series',
            description: 'Series de 400 metros',
            date: new Date(new Date().setDate(new Date().getDate() + 1)).toISOString(),
            duration: 30,
            distance: 5,
            avgPace: '4:30',
            avgHeartRate: 170,
            rpe: 7,
            notes: 'Intenso'
        },
    ]);

    const [trainingToEdit, setTrainingToEdit] = useState(null);

    useEffect(() => {
        if (!user) {
            navigate('/auth/login');
        }
    }, [user, navigate]);

    // Función para añadir un nuevo entrenamiento
    const handleAddTraining = useCallback((newTraining) => {
        console.log('Añadiendo entrenamiento:', newTraining);
        const trainingWithId = { ...newTraining, id: uuidv4() };
        setTrainings((prevTrainings) => [...prevTrainings, trainingWithId]);
    }, []);

    // Función para editar un entrenamiento existente
    const handleEditTraining = useCallback((updatedTraining) => {
        console.log('Editando entrenamiento:', updatedTraining);
        setTrainings((prevTrainings) =>
            prevTrainings.map((training) =>
                training.id === updatedTraining.id ? updatedTraining : training
            )
        );
    }, []);

    // Función para eliminar un entrenamiento
    const handleDelete = useCallback((id) => {
        setTrainings((prevTrainings) => prevTrainings.filter(training => training.id !== id));
    }, []);

    // Función para manejar la duplicación de un entrenamiento
    const handleDuplicateTraining = useCallback((training) => {
        const duplicatedTraining = {
            ...training,
            id: uuidv4(),
            date: new Date().toISOString(),
            // Asegúrate de que el 'type' sea válido
            type: training.type === 'Carrera' ? 'Carrera Continua' : training.type,
        };
        handleAddTraining(duplicatedTraining);
        console.log('Training duplicado:', duplicatedTraining);
    }, [handleAddTraining]);

    // Función para manejar el arrastre y soltar de eventos
    const handleEventDrop = useCallback((eventId, newStart, newEnd) => {
        setTrainings((prevTrainings) => prevTrainings.map(event =>
            event.id === eventId
                ? {
                    ...event,
                    date: newStart.toISOString().slice(0, 16),
                }
                : event
        ));
    }, []);

    // Función para manejar el clic en el botón "Agregar" en el calendario
    const handleAddClick = useCallback((date) => {
        setTrainingToEdit({ date: date.toISOString().slice(0, 16) });
    }, []);

    // Función para manejar el clic en un evento del calendario
    const handleEventClick = useCallback((event) => {
        console.log('Evento clicado:', event);
        setTrainingToEdit(event);
    }, []);

    // Función para cancelar la edición
    const handleCancelEdit = useCallback(() => {
        setTrainingToEdit(null);
    }, []);

    return (
        <Box sx={{ display: 'flex', justifyContent: 'center', padding: 3 }}>
            <Box sx={{ width: '100%', maxWidth: '1200px' }}>
                <Typography variant="h4" gutterBottom>Planifica tus entrenamientos</Typography>
                <TrainingCalendar
                    events={trainings}
                    onEventDrop={handleEventDrop}
                    onAddTraining={handleAddTraining}
                    onEditTraining={handleEditTraining}
                    onAddClick={handleAddClick}
                    onEventClick={handleEventClick}
                    trainingToEdit={trainingToEdit}
                    setTrainingToEdit={setTrainingToEdit}
                />
                <TrainingList
                    trainings={trainings}
                    handleEdit={handleEditTraining}
                    handleDelete={handleDelete}
                    handleDuplicate={handleDuplicateTraining}
                />
            </Box>
        </Box>
    );

};

export default TrainingPage;
